import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { HandIcon, PaintIcon, RubberIcon } from "@l2r-front/l2r-icons"
import {
    REFERENTIAL_MAP_ID,
    Legend,
    Draw,
    FREE_ROAM_MODE,
    PAINT_MODE,
    RUBBER_MODE,
    LateralizedFreeRoamMode,
    LateralizedStabiloMode,
    LateralizedRubberMode,
    useMapDispatchContext,
} from "@l2r-front/l2r-map"
import { NetworkReferentialLayer, ReferentialMap, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { RoadsideInventory } from "@l2r-front/l2r-proptypes"
import { UiModal, useIsMobileDevice, useTheme } from "@l2r-front/l2r-ui"
import { CatalogItem } from "@l2r-front/l2r-utils"


import { API_MODULE_ROADSIDE } from "../../../../common/constants/appRoad"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRoadsideType } from "../../hooks/queries/useRoadsideTypes"
import { useRoadRoadsideInventoriesFeatures } from "../../hooks/utils/useRoadRoadsideInventoriesFeatures"

import { RoadRoadsideInventoriesLayer } from "../RoadRoadsideInventoriesLayer"
import { RoadsideLayer } from "../RoadsideLayer"
import {
    createInventoryItem,
    groupFeaturesByDirection,
    haveFeaturesChanged,
    subtractMultipleRanges,
} from "../RoadsidePresenceEditModal/RoadsidePresenceEditModal.helper"

import * as Styled from "./RoadsideStateEditModal.styled"

type RoadsideStateEditModalProps = {
    className?: string,
    open: boolean,
    type: CatalogItem,
    onClose: () => void,
    onSave: (inventoryItems: RoadsideInventory[], parentType: string) => void,
    showLoader?: boolean,
}

export function RoadsideStateEditModal(props: RoadsideStateEditModalProps) {

    const {
        className,
        open,
        type,
        onClose,
        onSave,
        showLoader = false,
    } = props

    const [saveEnable, setSaveEnable] = useState(false)
    const [inventoryItems, setInventoryItems] = useState<RoadsideInventory[]>([])
    const { selectedSubNetworkSlugs } = useNetworksStateContext()
    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()
    const theme = useTheme()
    const { road } = useParams()
    const { getMapRef } = useMapDispatchContext()
    const mapRef = getMapRef(REFERENTIAL_MAP_ID)
    const [mapZoom, setMapZoom] = useState(0)
    const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null)

    const inventoriesFeatures = useRoadRoadsideInventoriesFeatures(road, type.code, { includeNetworkFeatures: false })

    const { data: parentType } = useRoadsideType(type.parent)

    const parentTypeName = parentType?.name || ""

    const handleFeaturesChanged = useCallback((featureCollection: GeoJSON.FeatureCollection) => {
        const result: RoadsideInventory[] = []

        // Get features from featureCollection (drawn features)
        const drawnFeatures = featureCollection.features || []
        const drawnByDirection = groupFeaturesByDirection(drawnFeatures)

        // Get inventoriesFeatures (existing inventory features)
        const existingInventoryFeatures = inventoriesFeatures?.features || []
        const inventoryByDirection = groupFeaturesByDirection(existingInventoryFeatures)

        // Process each direction separately
        for (const direction of ["+", "-"] as const) {
            const drawnInDirection = drawnByDirection[direction]
            const inventoryInDirection = inventoryByDirection[direction]

            // Get all drawn ranges for this direction
            const drawnRanges: [number, number][] = drawnInDirection.map(d => d.normalizedRange)

            // 1. Add all drawn features as RoadsideInventory items with their condition
            for (const drawnItem of drawnInDirection) {
                const { linearLoc, normalizedRange, feature } = drawnItem
                const condition = feature.properties?.["condition"] ?? 1

                result.push(createInventoryItem(
                    linearLoc.road,
                    normalizedRange[0],
                    normalizedRange[1],
                    direction,
                    type.code,
                    condition
                ))
            }

            // 2. For inventoriesFeatures: Keep only parts that DON'T overlap with drawn features
            for (const inventoryItem of inventoryInDirection) {
                const { linearLoc, normalizedRange, feature } = inventoryItem
                const condition = feature.properties?.["condition"] ?? 1

                const remainingRanges = subtractMultipleRanges(normalizedRange, drawnRanges)

                for (const remaining of remainingRanges) {
                    result.push(createInventoryItem(
                        linearLoc.road,
                        remaining[0],
                        remaining[1],
                        direction,
                        type.code,
                        condition
                    ))
                }
            }
        }

        setInventoryItems(result)

        // Compare result features with inventoriesFeatures to check if there are changes
        const hasChanges = haveFeaturesChanged(inventoriesFeatures?.features || [], result, type.code, true)
        setSaveEnable(hasChanges)
    }, [inventoriesFeatures, type.code])

    const handleMapZoom = useCallback(() => {
        const zoom = mapRef?.getMap().getZoom() || 0
        setMapZoom(zoom)
    }, [mapRef])

    const handleSave = useCallback(() => {
        if (inventoryItems.length) {
            onSave(inventoryItems, type.code)
        }
    }, [onSave, inventoryItems, type.code])

    const buttons = useMemo(() => {
        return <>
            <Styled.Button id={"button-cancel"} isMobile={isMobile} variant="tertiary" onClick={onClose}>{t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.cancelButton")}</Styled.Button>
            <Styled.Button id={"button-ok"} isMobile={isMobile} disabled={!saveEnable} onClick={handleSave}>{t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.saveButton")}</Styled.Button>
        </>
    }, [handleSave, isMobile, onClose, t, saveEnable])

    const modesConfig = {
        [FREE_ROAM_MODE]: {
            mode: LateralizedFreeRoamMode,
            icon: <HandIcon />,
            brushes: [{ name: "black", token: "common/black_states", properties: { condition: 0 }, }],
        },
        [PAINT_MODE]: {
            mode: LateralizedStabiloMode,
            icon: <PaintIcon />,
            brushes: [
                {
                    name: "green",
                    token: "brand logiroad/primary/500",
                    properties: { condition: 10 },
                },
                {
                    name: "orange",
                    token: "_components/legend/DPE/orange",
                    properties: { condition: 20 },
                },
                {
                    name: "red",
                    token: "error",
                    properties: { condition: 30 },
                },
                {
                    name: "blue",
                    token: "_components/legend/referential/trace",
                    properties: { condition: 1 },
                },
            ],
        },
        [RUBBER_MODE]: {
            mode: LateralizedRubberMode,
            icon: <RubberIcon />,
            brushes: [{ name: "black", token: "common/black_states", properties: { condition: 0 }, }],
        },
    }

    const displayedLegends = useMemo(() => {
        return [{
            layerName: "roadsidePresence",
            title: t(I18N_NAMESPACE, "container.roadsideStateEditModal.legend.title"),
            legendData: [
                {
                    color: theme.palette["brand logiroad/primary/500"].main,
                    label: t(I18N_NAMESPACE, "containers.roadside.legend.scale.good"),
                },
                {
                    color: theme.palette["_components/legend/DPE/orange"].main,
                    label: t(I18N_NAMESPACE, "containers.roadside.legend.scale.poor"),
                },
                {
                    color: theme.palette["error"].main,
                    label: t(I18N_NAMESPACE, "containers.roadside.legend.scale.serious"),
                },
                {
                    color: theme.palette["_components/legend/referential/trace"].main,
                    label: t(I18N_NAMESPACE, "containers.roadside.legend.scale.presence"),
                },
            ],
        }]
    }, [t, theme])

    const stabiloLayerStyle =
        [
            "case",
            ["==", ["get", "condition", ["properties"]], 1],
            theme.palette["_components/legend/referential/trace"].main,
            ["==", ["get", "condition", ["properties"]], 10],
            theme.palette["brand logiroad/primary/500"].main,
            ["==", ["get", "condition", ["properties"]], 20],
            theme.palette["_components/legend/DPE/orange"].main,
            ["==", ["get", "condition", ["properties"]], 30],
            theme.palette["error"].main,
            theme.palette["_components/legend/referential/trace"].main,
        ]

    return (
        <UiModal
            buttons={buttons}
            className={className}
            desc={t(I18N_NAMESPACE, "container.roadsideStateEditModal.desc")}
            open={open}
            onClose={onClose}
            showLoader={showLoader}
            title={t(I18N_NAMESPACE, "container.roadsideStateEditModal.title", { type: `${parentTypeName} / ${type?.["name"]}` })}
        >
            <ReferentialMap
                topLeftElements={[<Styled.DrawContainer key="draw-container" ref={setContainerElement} />, <Legend key="city-map-legend" displayedLegends={displayedLegends} openByDefault={!isMobile} />]}
                onZoom={handleMapZoom}>
                <NetworkReferentialLayer
                    interactive={false}
                    mapId={REFERENTIAL_MAP_ID}
                    module={API_MODULE_ROADSIDE}
                    subNetworks={selectedSubNetworkSlugs}
                    roadToHide={road} />
                <RoadsideLayer mapId={REFERENTIAL_MAP_ID} />
                <RoadRoadsideInventoriesLayer
                    road={road}
                    type={type}
                    mode="state"
                    filterNonWritableFeatures={false}
                    mapId={REFERENTIAL_MAP_ID}
                    mapZoom={mapZoom} />
                {!!inventoriesFeatures && <Draw
                    container={containerElement}
                    defaultMode={PAINT_MODE}
                    modesConfig={modesConfig}
                    lineColorStyle={stabiloLayerStyle}
                    opts={{ roadSegments: inventoriesFeatures }}
                    onFeaturesChange={handleFeaturesChanged}
                />}
            </ReferentialMap>
        </UiModal>
    )
}